import { useEffect, useCallback, useRef } from 'react'

interface UseBarcodeScanner {
  onScan: (barcode: string) => void
  enabled?: boolean
  minLength?: number
  maxDelay?: number
}

/**
 * Hook for USB/Bluetooth barcode scanner support (Keyboard Wedge Mode)
 * 
 * USB barcode scanners typically work as "keyboard wedge" devices - they type
 * the barcode characters very quickly followed by Enter. This hook detects
 * rapid keystroke sequences that end with Enter and treats them as barcode scans.
 * 
 * @param onScan - Callback function when a barcode is scanned
 * @param enabled - Whether the scanner is enabled (default: true)
 * @param minLength - Minimum barcode length to accept (default: 3)
 * @param maxDelay - Maximum delay between keystrokes in ms (default: 50)
 */
export const useBarcodeScanner = ({
  onScan,
  enabled = true,
  minLength = 3,
  maxDelay = 50
}: UseBarcodeScanner) => {
  const barcodeBuffer = useRef('')
  const lastKeyTime = useRef(Date.now())
  const isProcessing = useRef(false)

  const resetBuffer = useCallback(() => {
    barcodeBuffer.current = ''
  }, [])

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!enabled || isProcessing.current) return

    // Skip if user is typing in an input field (unless it's a dedicated barcode input)
    const target = event.target as HTMLElement
    const isBarcodeinput = target.getAttribute('data-barcode-input') === 'true'
    
    if (
      !isBarcodeinput &&
      (target.tagName === 'INPUT' || 
       target.tagName === 'TEXTAREA' || 
       target.isContentEditable)
    ) {
      return
    }

    const currentTime = Date.now()
    const timeDiff = currentTime - lastKeyTime.current

    // If more than maxDelay ms between keystrokes, reset buffer
    // USB scanners type very fast (usually < 30ms between characters)
    if (timeDiff > maxDelay) {
      barcodeBuffer.current = ''
    }
    lastKeyTime.current = currentTime

    if (event.key === 'Enter') {
      // Barcode complete - scanner sends Enter at the end
      const barcode = barcodeBuffer.current.trim()
      
      if (barcode.length >= minLength) {
        event.preventDefault()
        event.stopPropagation()
        
        isProcessing.current = true
        onScan(barcode)
        
        // Reset after a short delay to prevent double-processing
        setTimeout(() => {
          isProcessing.current = false
        }, 100)
      }
      
      barcodeBuffer.current = ''
    } else if (event.key.length === 1 && !event.ctrlKey && !event.altKey && !event.metaKey) {
      // Add character to buffer (only printable characters)
      barcodeBuffer.current += event.key
    }
  }, [onScan, enabled, minLength, maxDelay])

  useEffect(() => {
    if (!enabled) {
      resetBuffer()
      return
    }

    // Use capture phase to catch events before they reach input fields
    window.addEventListener('keydown', handleKeyDown, { capture: true })
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown, { capture: true })
    }
  }, [handleKeyDown, enabled, resetBuffer])

  return {
    resetBuffer,
    isEnabled: enabled
  }
}

export default useBarcodeScanner
